import { Component, inject, OnInit, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { CardModule } from 'primeng/card';
import { TabsModule } from 'primeng/tabs';
import { interval, Subject, takeUntil } from 'rxjs';
import { Permissions } from '../../../constants/permissions';
import { IBms } from '../../../models/bms';
import { IControllerDetail } from '../../../models/controller';
import { IEms } from '../../../models/ems';
import { IEnergyMeterList } from '../../../models/energy-meter';
import { IInverterList } from '../../../models/inverter';
import { IIo } from '../../../models/io';
import {
  IAnalogIOExtenderList,
  IGen2IOExtenderList,
  INormalIOExtenderList,
} from '../../../models/io-extender';
import { Auth, UserRoles } from '../../../services/auth';
import { Bms } from '../../../services/bms';
import { Controller } from '../../../services/controller';
import { Ems } from '../../../services/ems';
import { EnergyMeter } from '../../../services/energy-meter';
import { ExtIo } from '../../../services/ext-io';
import { Inverter } from '../../../services/inverter';
import { Io } from '../../../services/io';
import { CommandType, SocketService } from '../../../services/socket';
import { BmsTable } from './bms-table/bms-table';
import { ControllerTable } from './controller-table/controller-table';
import { DInputsTable } from './d-inputs-table/d-inputs-table';
import { DOutputsTable } from './d-outputs-table/d-outputs-table';
import { EmsTable } from './ems-table/ems-table';
import { EnergyMeterTable } from './energy-meter-table/energy-meter-table';
import { EnergyMeterView } from './energy-meter-view/energy-meter-view';
import { History } from './history/history';
import { InverterTable } from './inverter-table/inverter-table';
import { IoAddonView } from './io-addon-view/io-addon-view';
import { Tools } from './tools/tools';

@Component({
  selector: 'app-detail',
  imports: [
    CardModule,
    EnergyMeterView,
    ControllerTable,
    InverterTable,
    EnergyMeterTable,
    EmsTable,
    BmsTable,
    DOutputsTable,
    DInputsTable,
    TabsModule,
    IoAddonView,
    History,
    Tools,
  ],
  templateUrl: './detail.html',
  styleUrl: './detail.css',
})
export class Detail implements OnInit {
  private activatedRoute = inject(ActivatedRoute);
  private controllerId = signal<number>(0);
  private destroy$ = new Subject<void>();

  readonly controllerService = inject(Controller);
  readonly inverterService = inject(Inverter);
  readonly energyMeterService = inject(EnergyMeter);
  readonly emsService = inject(Ems);
  readonly bmsService = inject(Bms);
  readonly ioService = inject(Io);
  readonly extIOService = inject(ExtIo);
  readonly socketService = inject(SocketService);
  readonly authService = inject(Auth);
  readonly permissions = Permissions;
  readonly showAdminFields =
    this.authService.hasRole(UserRoles.ADMIN) ||
    this.authService.hasRole(UserRoles.SUPER_ADMIN);

  hasPermission(permission: Permissions): boolean {
    return this.authService.hasPermission(permission);
  }

  message = '';
  messages: string[] = [];

  constructor(titleService: Title) {
    titleService.setTitle('Dashboard');
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe((params) => {
      this.controllerId.set(params['controllerId']);
      this.controllerService.getControllerDetails(this.controllerId());
      this.inverterService.getInverterList(this.controllerId());
      this.energyMeterService.getEnergyMeterList(this.controllerId());
      this.emsService.getEMSList(this.controllerId());
      this.bmsService.getBMSList(this.controllerId());
      this.ioService.getIOInformation(this.controllerId());
      this.energyMeterService.getEnergyMeterLogAllMeters(this.controllerId());
      this.extIOService.getNormalIOExtenders(this.controllerId());
      this.extIOService.getAnalogIOExtenders(this.controllerId());
      this.extIOService.getGen2IOExtenders(this.controllerId());
    });

    this.socketService.getConnectionStatus().subscribe((isConnected) => {
      console.log(isConnected ? '🟢 Connected' : '🔴 Disconnected');

      if (isConnected) {
        this.createIntervalTimer();
      }
    });

    this.createListeners();
  }

  createIntervalTimer() {
    interval(5000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const requestData = { boxId: this.controllerId() };
        this.socketService.emitRequestMessage(
          CommandType.get_controller,
          requestData,
        );
        this.socketService.emitRequestMessage(
          CommandType.get_energy_meter,
          requestData,
        );
        this.socketService.emitRequestMessage(CommandType.get_ems, requestData);
        this.socketService.emitRequestMessage(CommandType.get_bms, requestData);
        this.socketService.emitRequestMessage(
          CommandType.get_inverter,
          requestData,
        );
        this.socketService.emitRequestMessage(CommandType.get_io, requestData);
        this.socketService.emitRequestMessage(
          CommandType.get_ext_io_normal,
          requestData,
        );
        this.socketService.emitRequestMessage(
          CommandType.get_ext_io_analog,
          requestData,
        );
        this.socketService.emitRequestMessage(
          CommandType.get_ext_io_gen2,
          requestData,
        );
        // this.socketService.emitRequestMessage(
        //   CommandType.get_energy_meter_log,
        //   requestData
        // );
      });
  }

  createListeners() {
    this.socketService
      .listen<IControllerDetail>(CommandType.get_controller)
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        this.controllerService.updateFromSocket(msg);
      });

    this.socketService
      .listen<IEnergyMeterList[]>(CommandType.get_energy_meter)
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        this.energyMeterService.updateFromSocket(msg);
      });

    this.socketService
      .listen<IEms[]>(CommandType.get_ems)
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        this.emsService.updateFromSocket(msg);
      });

    this.socketService
      .listen<IBms[]>(CommandType.get_bms)
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        this.bmsService.updateFromSocket(msg);
      });

    this.socketService
      .listen<IInverterList[]>(CommandType.get_inverter)
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        this.inverterService.updateFromSocket(msg);
      });

    this.socketService
      .listen<IIo>(CommandType.get_io)
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        this.ioService.updateFromSocket(msg);
      });

    this.socketService
      .listen<INormalIOExtenderList>(CommandType.get_ext_io_normal)
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        this.extIOService.updateNormalFromSocket(msg);
      });

    this.socketService
      .listen<IAnalogIOExtenderList>(CommandType.get_ext_io_analog)
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        this.extIOService.updateAnalogFromSocket(msg);
      });

    this.socketService
      .listen<IGen2IOExtenderList>(CommandType.get_ext_io_gen2)
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        this.extIOService.updateGen2FromSocket(msg);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
